import { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { API } from "../../App";
import { Button } from "../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Calendar } from "lucide-react";
import BookingDateFilter, { getDateRange } from "../../components/BookingDateFilter";
import OwnerBookingRow from "./OwnerBookingRow";

export default function OwnerBookingsPage() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [cancelReasons, setCancelReasons] = useState([]);
  const { t } = useTranslation();

  // Prefetch cancellation reasons once and pass down to each row.
  useEffect(() => {
    axios.get(`${API}/cancellation-reasons`).then((res) => setCancelReasons(res.data)).catch(() => {});
  }, []);

  const fetchBookings = async () => {
    try {
      setFetchError(null);
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      const { date_from, date_to } = getDateRange(dateFilter);
      if (date_from) params.set("date_from", date_from);
      if (date_to) params.set("date_to", date_to);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const response = await axios.get(`${API}/owner/bookings${qs}`);
      setBookings(response.data.bookings || []);
    } catch (error) {
      console.error("Error fetching bookings:", error);
      // 404 "No venue assigned" is the most common failure mode here — the
      // user's localStorage active_venue_id may be stale (e.g. they're a
      // super_admin who lost direct ownership of a venue). Surfacing this
      // explicitly is far better than rendering "No bookings" silently.
      if (error.response?.status === 404) {
        try { localStorage.removeItem("active_venue_id"); } catch { /* noop */ }
        setFetchError(
          error.response?.data?.detail ||
            t("owner:no_venue_assigned", {
              defaultValue: "No venue is assigned to your account. Please contact an admin.",
            }),
        );
      } else {
        setFetchError(t("owner:bookings_fetch_failed", { defaultValue: "Could not load bookings — please retry." }));
      }
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBookings(); }, [statusFilter, dateFilter]); // eslint-disable-line

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="animate-pulse bg-muted h-24 rounded-xl" />
        ))}
      </div>
    );
  }

  return (
    <div data-testid="owner-bookings-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-medium text-foreground">{t("owner:bookings")}</h1>
        <div className="flex flex-wrap items-center gap-2">
          <BookingDateFilter value={dateFilter} onChange={setDateFilter} />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44" data-testid="booking-status-filter">
              <SelectValue placeholder={t("owner:all_status")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("owner:all_status")}</SelectItem>
              <SelectItem value="pending">{t("booking_status:pending")}</SelectItem>
              <SelectItem value="confirmed">{t("booking_status:confirmed")}</SelectItem>
              <SelectItem value="change_proposed">{t("booking_status:change_proposed")}</SelectItem>
              <SelectItem value="completed">{t("booking_status:completed")}</SelectItem>
              <SelectItem value="rejected">{t("booking_status:rejected")}</SelectItem>
              <SelectItem value="cancelled_by_customer">{t("booking_status:cancelled_by_customer")}</SelectItem>
              <SelectItem value="cancelled_by_venue">{t("booking_status:cancelled_by_venue")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {fetchError ? (
        <div
          className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center"
          data-testid="owner-bookings-error"
        >
          <p className="text-amber-800 font-medium">{fetchError}</p>
          <Button
            className="mt-3"
            variant="outline"
            onClick={() => { setLoading(true); fetchBookings(); }}
            data-testid="owner-bookings-retry"
          >
            {t("owner:retry", { defaultValue: "Retry" })}
          </Button>
        </div>
      ) : bookings.length > 0 ? (
        <div className="space-y-4">
          {bookings.map((booking) => (
            <OwnerBookingRow
              key={booking.booking_id}
              booking={booking}
              onChanged={fetchBookings}
              cancelReasons={cancelReasons}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-card rounded-xl border border-border">
          <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">{t("owner:no_bookings")}</h3>
          <p className="text-muted-foreground">{t("owner:no_bookings_sub")}</p>
        </div>
      )}
    </div>
  );
}
